/**
 * AchievementSystem.ts — Achievement tracking, evaluation, and unlocking.
 *
 * Evaluates achievement requirements against game state and vault data.
 * Supports 11 requirement types: counter, flag, nodes_visited, playtime,
 * trait, stat_threshold, class, all_classes_completed, level, unique_endings, ending.
 *
 * Per-character achievements use GameState counters/flags.
 * Cross-playthrough achievements (account scope) use the vault.
 */

import type {
  Achievement,
  AchievementCategory,
  AchievementRequirement,
} from '@game/data/types';
import { achievements, getAchievement, getTotalPoints } from '@game/data/achievements';
import type { GameState } from '@core/engine/GameState';
import type { GallowcrestVault } from '@core/engine/SaveLoad';
import type { EventSystem } from '@core/engine/EventSystem';

/** Status of a single achievement for display purposes. */
export interface AchievementStatus {
  achievement: Achievement;
  unlocked: boolean;
  /** Progress fraction 0-1 for tiered/counter achievements. */
  progress: number;
  /** Human-readable progress text (e.g., "5/25 victories"). */
  progressText?: string;
}

export class AchievementSystem {
  private events: EventSystem;

  constructor(events: EventSystem) {
    this.events = events;
  }

  /**
   * Check achievements and unlock any whose requirements are met.
   * @param timing — 'mid_adventure' checks only mid-game achievements,
   *   'completion' checks only end-game achievements,
   *   'all' checks everything (used for vault-only display, not gameplay).
   * Returns newly unlocked achievement IDs.
   */
  checkAll(
    state: GameState,
    vault: GallowcrestVault,
    timing: 'mid_adventure' | 'completion' | 'all' = 'mid_adventure',
  ): string[] {
    const newlyUnlocked: string[] = [];

    for (const achievement of achievements) {
      if (vault.unlockedAchievements.includes(achievement.id)) continue;

      // Filter by timing: mid_adventure checks skip completion-only achievements
      if (timing !== 'all' && achievement.unlockTiming !== 'any' && achievement.unlockTiming !== timing) {
        continue;
      }

      if (this.evaluate(achievement, state, vault)) {
        vault.unlockedAchievements.push(achievement.id);
        newlyUnlocked.push(achievement.id);

        this.events.emit('achievementUnlocked', {
          id: achievement.id,
          name: achievement.name,
          description: achievement.description,
          rarity: achievement.rarity,
          points: achievement.points,
        });
      }
    }

    return newlyUnlocked;
  }

  /**
   * Evaluate whether a single achievement's requirements are all met.
   */
  evaluate(
    achievement: Achievement,
    state: GameState,
    vault: GallowcrestVault,
  ): boolean {
    return achievement.requirements.every(req =>
      this.evaluateRequirement(req, state, vault),
    );
  }

  /**
   * Evaluate a single requirement against game state and vault.
   */
  evaluateRequirement(
    req: AchievementRequirement,
    state: GameState,
    vault: GallowcrestVault,
  ): boolean {
    switch (req.type) {
      case 'counter':
        return this.evaluateCounter(req, state);

      case 'flag':
        return this.evaluateFlag(req, state);

      case 'nodes_visited':
        return this.compareValue(
          state.story.visitedNodes.size,
          req.operator ?? '>=',
          Number(req.value),
        );

      case 'playtime':
        return this.compareValue(
          state.playtime,
          req.operator ?? '>=',
          Number(req.value),
        );

      case 'trait':
        return this.evaluateTrait(req, state);

      case 'stat_threshold':
        return this.evaluateStatThreshold(req, state);

      case 'class':
        return state.character.classId === String(req.value);

      case 'level':
        return this.compareValue(
          state.character.level,
          req.operator ?? '>=',
          Number(req.value),
        );

      case 'ending':
        return vault.completions.some(c => c.endingId === String(req.value));

      case 'unique_endings':
        return this.evaluateUniqueEndings(req, vault);

      case 'all_classes_completed':
        return this.evaluateAllClassesCompleted(vault);

      default:
        return false;
    }
  }

  // ─── Specific Evaluators ─────────────────────────────────

  private evaluateCounter(req: AchievementRequirement, state: GameState): boolean {
    const key = req.key ?? '';
    const current = state.achievementCounters.get(key) ?? 0;
    return this.compareValue(current, req.operator ?? '>=', Number(req.value));
  }

  private evaluateFlag(req: AchievementRequirement, state: GameState): boolean {
    const key = req.key ?? '';
    const current = state.achievementFlags.get(key) ?? false;
    const expected = req.value === true || req.value === 'true';
    return current === expected;
  }

  private evaluateTrait(req: AchievementRequirement, state: GameState): boolean {
    const key = req.key ?? '';
    const trait = state.getTrait(key);
    if (!trait) return false;
    return this.compareValue(trait.level, req.operator ?? '>=', Number(req.value));
  }

  private evaluateStatThreshold(req: AchievementRequirement, state: GameState): boolean {
    const key = req.key ?? '';
    // Handle special stat names like corruption_points
    switch (key) {
      case 'corruption_points':
      case 'corruption':
        return this.compareValue(state.character.corruption, req.operator ?? '>=', Number(req.value));
      case 'frostbite':
        return this.compareValue(state.character.frostbite, req.operator ?? '>=', Number(req.value));
      default:
        // Try as a regular stat
        const statValue = state.getEffectiveStat(key as any);
        return this.compareValue(statValue, req.operator ?? '>=', Number(req.value));
    }
  }

  private evaluateUniqueEndings(req: AchievementRequirement, vault: GallowcrestVault): boolean {
    const uniqueEndings = new Set(vault.completions.map(c => c.endingId));
    return this.compareValue(uniqueEndings.size, req.operator ?? '>=', Number(req.value));
  }

  private evaluateAllClassesCompleted(vault: GallowcrestVault): boolean {
    const completedClasses = new Set(vault.completions.map(c => c.classId));
    return completedClasses.size >= 5;
  }

  // ─── Counter/Flag Management ─────────────────────────────

  /** Increment an achievement counter. */
  incrementCounter(key: string, state: GameState, amount: number = 1): void {
    const current = state.achievementCounters.get(key) ?? 0;
    state.achievementCounters.set(key, current + amount);
  }

  /** Set an achievement flag. */
  setFlag(key: string, state: GameState, value: boolean = true): void {
    state.achievementFlags.set(key, value);
  }

  /** Get the current value of a counter. */
  getCounter(key: string, state: GameState): number {
    return state.achievementCounters.get(key) ?? 0;
  }

  /** Get the current value of a flag. */
  getFlag(key: string, state: GameState): boolean {
    return state.achievementFlags.get(key) ?? false;
  }

  // ─── Status & Display ────────────────────────────────────

  /**
   * Get status for all achievements, optionally filtered by category.
   */
  getStatuses(
    state: GameState,
    vault: GallowcrestVault,
    category?: AchievementCategory,
  ): AchievementStatus[] {
    let list = achievements;
    if (category) {
      list = list.filter(a => a.category === category);
    }

    return list.map(achievement => this.getStatus(achievement, state, vault));
  }

  /**
   * Get status for a single achievement.
   */
  getStatus(
    achievement: Achievement,
    state: GameState,
    vault: GallowcrestVault,
  ): AchievementStatus {
    const unlocked = vault.unlockedAchievements.includes(achievement.id);

    // Calculate progress for counter-based requirements
    let progress = unlocked ? 1 : 0;
    let progressText: string | undefined;

    if (!unlocked && achievement.requirements.length > 0) {
      const req = achievement.requirements[0];
      if (req.type === 'counter' && req.key) {
        const current = state.achievementCounters.get(req.key) ?? 0;
        const target = Number(req.value);
        progress = Math.min(1, target > 0 ? current / target : 0);
        progressText = `${current}/${target}`;
      } else if (req.type === 'nodes_visited') {
        const current = state.story.visitedNodes.size;
        const target = Number(req.value);
        progress = Math.min(1, target > 0 ? current / target : 0);
        progressText = `${current}/${target} nodes`;
      } else if (req.type === 'level') {
        const current = state.character.level;
        const target = Number(req.value);
        progress = Math.min(1, target > 0 ? current / target : 0);
        progressText = `Level ${current}/${target}`;
      } else if (req.type === 'unique_endings') {
        const uniqueEndings = new Set(vault.completions.map(c => c.endingId));
        const target = Number(req.value);
        progress = Math.min(1, target > 0 ? uniqueEndings.size / target : 0);
        progressText = `${uniqueEndings.size}/${target} endings`;
      }
    }

    return { achievement, unlocked, progress, progressText };
  }

  /** Get the total unlocked points. */
  getUnlockedPoints(vault: GallowcrestVault): number {
    let total = 0;
    for (const id of vault.unlockedAchievements) {
      const achievement = getAchievement(id);
      if (achievement) total += achievement.points;
    }
    return total;
  }

  /** Get total possible points (re-exports from data module). */
  getTotalPoints(): number {
    return getTotalPoints();
  }

  /** Get count of unlocked achievements. */
  getUnlockedCount(vault: GallowcrestVault): number {
    return vault.unlockedAchievements.length;
  }

  /** Get total achievement count. */
  getTotalCount(): number {
    return achievements.length;
  }

  // ─── Internal ────────────────────────────────────────────

  private compareValue(
    current: number,
    operator: string,
    target: number,
  ): boolean {
    switch (operator) {
      case '>=': return current >= target;
      case '<=': return current <= target;
      case '>': return current > target;
      case '<': return current < target;
      case '==':
      case 'equals': return current === target;
      case '!=': return current !== target;
      default: return current >= target;
    }
  }
}
